import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { BannerConfigModel, BannersConfigModel } from '../../../models/section-configuration.model';
import { MatDialog } from '@angular/material/dialog';
import { ErrorNotificationDialogComponent } from 'src/app/modules/shared/dialogs/error-notification-dialog/error-notification-dialog.component';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { ConfirmationDialogComponent } from 'src/app/modules/shared/dialogs/confirmation-dialog/confirmation-dialog.component';
import { SectionConfigurationService } from '../../../services/section-configuration.service';
import { SnackbarService } from '../../../../shared/services/snackbar.service';

@Component({
    selector: 'banners-list',
    templateUrl: 'banners-list.component.html',
    styleUrls: ['banners-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BannersListComponent implements OnInit {
  @Input() bannersConfig: BannersConfigModel;
  @Input() sectionConfigId: string;
  @Input() parentFormGroup: FormGroup;

  constructor(private readonly configService: SectionConfigurationService,
    private readonly dialog: MatDialog,
    private readonly fb: FormBuilder,
    private readonly snackbarService: SnackbarService
  ) { }

  ngOnInit(): void {
    this.addControls();
  }

  get containsExpiredBanners() {
    return (<BannersConfigModel>this.parentFormGroup.getRawValue()).items.some(item => this.isBannerExpired(item));
  }

  isBannerExpired(item: BannerConfigModel): boolean {
    return !!item.endDate && new Date(item.endDate) < new Date() && !!item.src;
  }

  isBannerFormExpired(index: number): boolean {
    const formGroup = this.getFormGroup(index);
    const formValue = formGroup?.getRawValue() as BannerConfigModel;
    const item = formGroup?.contains('endDate') ? formValue : this.bannersConfig.items[index];
    return this.isBannerExpired(item);
  }

  getFormGroup(i: number): FormGroup {
    return this.getFormArrayElements().controls[i] as FormGroup;
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.bannersConfig.items, event.previousIndex, event.currentIndex);

    const formArray = this.getFormArrayElements();
    let group = formArray.at(event.previousIndex);
    formArray.removeAt(event.previousIndex);
    formArray.insert(event.currentIndex, group);
  }

  addNewBanner(event: Event): void {
    event.preventDefault();
    this.getFormArrayElements().insert(0, this.fb.group({}));
    this.bannersConfig.items.unshift({} as BannerConfigModel);
    this.parentFormGroup.markAsTouched();

  }

  deleteBanner(formGroup: FormGroup): void {
    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Delete banner',
        message: 'Are you sure you want to delete this banner?'
      }
    }).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        const bannerUrl = formGroup.getRawValue().src;
        if (bannerUrl) {
          this.deleteExistingBanner(bannerUrl, formGroup);
        } else {
          this.deleteItemFromFormArray(formGroup);
        }
      }
    });
  }

  deleteExpiredBanners(event: Event): void {
    event.preventDefault();

    const expiredFormGroups = this.getFormArrayElements().controls
      .filter(control => this.isBannerExpired(control.getRawValue())) as FormGroup[];
    const expiredBannerUrls = expiredFormGroups.map(group => group.getRawValue().src);

    this.dialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Delete expired banners',
        message: `Are you sure you want to delete ${expiredBannerUrls.length} expired banner(s)?`
      }
    }).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.configService.deleteBanners(this.sectionConfigId, expiredBannerUrls).subscribe({
          next: () => {
            expiredFormGroups.forEach(group => this.deleteItemFromFormArray(group));

            this.showSuccessSnackBar();
          },
          error: () => this.dialog.open(ErrorNotificationDialogComponent)
        });
      }
    });
  }

  private deleteExistingBanner(bannerUrl: string, formGroup: FormGroup): void {
    this.configService.deleteBanners(this.sectionConfigId, [bannerUrl]).subscribe({
      next: () => {
        this.deleteItemFromFormArray(formGroup);
        this.showSuccessSnackBar();
      },
      error: () => this.dialog.open(ErrorNotificationDialogComponent)
    });
  }

  private deleteItemFromFormArray(formGroup: FormGroup): void {
    const formArray = this.getFormArrayElements();
    const index = formArray.controls.findIndex((control) => formGroup === control);

    if (index < 0) {
      return;
    }

    this.bannersConfig.items.splice(index, 1);
    formArray.removeAt(index);
  }

  private getFormArrayElements(): FormArray {
    return <FormArray>this.parentFormGroup.controls['items'];
  }

  private addControls(): void {
    this.parentFormGroup.addControl('items', this.fb.array([...this.bannersConfig.items.map(() => this.fb.group({}))]));
  }

  private showSuccessSnackBar(): void {
    this.snackbarService.success('COMMON.SAVE_SUCCESS');
  }
}
